/**
 * @module components/governance
 *
 * The governance section: four decision forums, a decision-rights matrix, the
 * measurable controls that make governance real, and the operating cadence.
 *
 * The RACI-style matrix is rendered as a real `<table>` with proper headers, so
 * it is navigable by screen readers and survivable when CSS is absent.
 */

import { MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import {
  FORUMS,
  CONTROLS,
  CADENCE,
  DECISION_RIGHTS,
  DECISION_ROLES,
  GOVERNANCE_SUMMARY,
} from "../data/governance.js";
import { icon } from "../svg/icons.js";
import { governanceLattice } from "../svg/scenes.js";

/** Human-readable expansion of the RACI letters. */
const ROLE_MEANING = {
  R: "Responsible — does the work",
  A: "Accountable — owns the decision",
  C: "Consulted — input before the decision",
  I: "Informed — told after the decision",
};

/**
 * Render one forum card.
 *
 * @param {import("../data/governance.js").Forum} forum
 * @returns {string} Markup.
 */
function forumMarkup(forum) {
  return `
    <li class="forum-card card" data-forum="${forum.id}" style="--forum-accent:${forum.accent}">
      <div class="forum-card__head">
        <h3 class="forum-card__name">${forum.name}</h3>
        <span class="forum-card__cadence">${forum.cadence}</span>
      </div>
      <p class="forum-card__mandate">${forum.mandate}</p>
      <ul class="forum-card__members" aria-label="Membership">
        ${forum.members.map((member) => `<li>${member}</li>`).join("")}
      </ul>
      <dl class="forum-card__detail">
        <div>
          <dt>Decides</dt>
          <dd>${forum.decides}</dd>
        </div>
        <div>
          <dt>Escalates</dt>
          <dd>${forum.escalates}</dd>
        </div>
      </dl>
    </li>`;
}

/**
 * Render the decision-rights table.
 *
 * @returns {string} Markup.
 */
function raciMarkup() {
  const head = DECISION_ROLES.map((role) => `<th scope="col">${role}</th>`).join("");
  const rows = DECISION_RIGHTS.map(
    (row) => `
    <tr>
      <th scope="row" class="raci__activity">${row.activity}</th>
      ${DECISION_ROLES.map((role) => {
        const value = row.roles[role] ?? "";
        return `<td><span class="raci__cell" data-role="${value}">${value}</span></td>`;
      }).join("")}
    </tr>`
  ).join("");

  return `
    <div class="raci">
      <table class="raci__table">
        <caption class="visually-hidden">
          Decision rights across the transformation: which forum is responsible, accountable,
          consulted or informed for each class of decision.
        </caption>
        <thead>
          <tr><th scope="col">Decision</th>${head}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <ul class="raci__legend">
      ${Object.entries(ROLE_MEANING)
        .map(
          ([letter, meaning]) =>
            `<li><span class="raci__cell" data-role="${letter}">${letter}</span>${meaning}</li>`
        )
        .join("")}
    </ul>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initGovernance() {
  const root = qs("[data-governance]");
  if (!root) return () => {};

  /* --- Copy -------------------------------------------------------------- */

  const eyebrow = qs("[data-gov-eyebrow]", root);
  const title = qs("[data-gov-title]", root);
  const lede = qs("[data-gov-lede]", root);
  if (eyebrow) eyebrow.textContent = GOVERNANCE_SUMMARY.eyebrow;
  if (title) title.textContent = GOVERNANCE_SUMMARY.title;
  if (lede) lede.textContent = GOVERNANCE_SUMMARY.lede;

  /* --- Scene ------------------------------------------------------------- */

  const scene = qs("[data-gov-scene]", root);
  if (scene) {
    scene.innerHTML = governanceLattice({
      forums: FORUMS.map((forum) => ({ id: forum.id, name: forum.name })),
      accent: "#5fd4c4",
      secondary: "#e0b584",
    });
  }

  /* --- Lists ------------------------------------------------------------- */

  const forumList = qs("[data-forums]", root);
  if (forumList) forumList.innerHTML = FORUMS.map(forumMarkup).join("");

  const raciArea = qs("[data-raci]", root);
  if (raciArea) raciArea.innerHTML = raciMarkup();

  const controlsList = qs("[data-controls]", root);
  if (controlsList) {
    controlsList.innerHTML = CONTROLS.map(
      (control) => `
      <li class="control-card card" data-control="${control.id}">
        <p class="control-card__name">${icon(control.glyph, { size: 17 })}${control.name}</p>
        <p class="control-card__body">${control.body}</p>
        <p class="control-card__evidence"><strong>Evidence</strong>${control.evidence}</p>
      </li>`
    ).join("");
  }

  const cadenceList = qs("[data-cadence]", root);
  if (cadenceList) {
    cadenceList.innerHTML = CADENCE.map(
      (item) => `
      <li class="cadence__row" data-cadence-row="${item.period.toLowerCase()}">
        <span class="cadence__period">${item.period}</span>
        <span class="cadence__forum">${item.forum}</span>
        <span class="cadence__output">${item.output}</span>
      </li>`
    ).join("");
  }

  /* --- Motion ------------------------------------------------------------ */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const cleanup = [];

    if (scene) {
      const plates = qsa(".lattice-plate", scene);
      const grid = qsa(".lattice-grid line", scene);
      const nodes = qsa(".lattice-node", scene);

      if (plates.length) {
        cleanup.push(
          watchReveal(plates, {
            from: { autoAlpha: 0, y: -14 },
            to: { autoAlpha: 1, y: 0 },
            duration: 0.7,
            stagger: 0.08,
            start: "top 84%",
          })
        );
      }

      if (grid.length) {
        cleanup.push(
          watchReveal(grid, {
            from: { drawSVG: "0%" },
            to: { drawSVG: "100%" },
            duration: 1.1,
            ease: "power1.inOut",
            stagger: 0.018,
            start: "top 84%",
          })
        );
      }

      if (nodes.length) {
        cleanup.push(
          watchReveal(nodes, {
            from: { autoAlpha: 0, scale: 0.4 },
            to: { autoAlpha: 1, scale: 1 },
            duration: 0.5,
            stagger: { each: 0.012, from: "center" },
            start: "top 84%",
          })
        );
      }
    }

    const cards = qsa(".forum-card, .control-card, .cadence__row", root);
    if (cards.length) {
      cleanup.push(
        watchReveal(cards, { from: { autoAlpha: 0, y: 18 }, duration: 0.6, stagger: 0.04, start: "top 80%" })
      );
    }

    return () => cleanup.forEach((dispose) => dispose());
  });

  return () => mm.revert();
}
